import { ContentTypeEnum, EditableFavoriteTypeEnum } from '@elearning-platform/api-types/enums';
import { ContentsUnion } from '@elearning-platform/api-types/interfaces';
import { CardGroupCard, CardIconsData, CardVariant, Color, ContentTableItem, IconName, UseState } from '@elearning-platform/ui';
import { useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { downloadFile } from '../api/requests';
import { AppConfig } from '../appConfig';
import {
  AppLanguage,
  FavouriteItemActionEnum,
  KnowledgeBaseViewMode,
  LessonStatus,
  LessonTableColumnName,
  RouteNameEnum,
  ThumbnailAward,
  ViewImage,
} from '../enums';
import { downloadAll } from '../helpers/zipDownload';
import { UseAuth, UseFavouriteItems, UseLessonCard, UseLessonCardOptions, UseRedirectTo } from '../interfaces';
import { getContentHash } from '../utils';

import { useAppLanguage } from './useAppLanguage.hook';
import { useAuth } from './useAuth.hook';
import { useFavouriteItems } from './useFavouriteItems.hook';
import { useRedirectTo } from './useRedirectTo.hook';

export const useLessonCard: (options?: UseLessonCardOptions) => UseLessonCard = (options?: UseLessonCardOptions): UseLessonCard => {
  const { withoutFavoriteButton }: UseLessonCardOptions = options ?? {};
  const [itemToShowOnSidePanel, setItemToShowOnSidePanel]: UseState<ContentsUnion | undefined> = useState<ContentsUnion | undefined>();
  const { redirect }: UseRedirectTo = useRedirectTo();
  const { t }: TransProps<never> = useTranslation();
  const { toggleFavouriteAsync }: UseFavouriteItems = useFavouriteItems();
  const { appSettings }: UseAuth = useAuth();
  const viewMode: KnowledgeBaseViewMode = appSettings?.knowledgeBaseViewMode ?? KnowledgeBaseViewMode.Grid;
  const language: AppLanguage = useAppLanguage();

  const getFavouriteItemType: (itemType: ContentTypeEnum) => EditableFavoriteTypeEnum = (
    itemType: ContentTypeEnum
  ): EditableFavoriteTypeEnum => {
    switch (itemType) {
      case ContentTypeEnum.contents:
        return EditableFavoriteTypeEnum.ContentGroup;
      case ContentTypeEnum.files:
        return EditableFavoriteTypeEnum.FileGroup;
      case ContentTypeEnum.file:
        return EditableFavoriteTypeEnum.File;
      case ContentTypeEnum.lesson:
      case ContentTypeEnum.lessonSlideScroll:
      case ContentTypeEnum.presentation:
      case ContentTypeEnum.video:
      case ContentTypeEnum.webinar:
        return EditableFavoriteTypeEnum.Content;
      default:
        itemType satisfies never;

        throw new Error(`Unhandled content type: ${itemType}`);
    }
  };

  const getCardImage: (item: ContentsUnion) => string = (item: ContentsUnion): string => {
    switch (item.type) {
      case ContentTypeEnum.files:
        return item.items?.length ? '/files.jpg' : '';
      case ContentTypeEnum.contents:
        return item.items?.length && item.items[0].image ? `${AppConfig.content}${item.items[0].image}` : '';
      case ContentTypeEnum.file:
        switch (item.url?.split('.').pop()) {
          case 'png':
            return ViewImage.PngCover;
          case 'jpg':
            return ViewImage.JpgCover;
          case 'docx':
            return ViewImage.DocCover;
          case 'pdf':
            return ViewImage.PdfCover;
          case 'xlsx':
            return ViewImage.XlsCover;
          case 'ppt':
            return ViewImage.PptCover;
          default:
            return '';
        }
      case ContentTypeEnum.lesson:
      case ContentTypeEnum.lessonSlideScroll:
      case ContentTypeEnum.presentation:
      case ContentTypeEnum.video:
      case ContentTypeEnum.webinar:
        return item.image ? `${AppConfig.content}${item.image}` : '';
      default:
        item satisfies never;

        return '';
    }
  };

  const getAwardIconColor: (award: ThumbnailAward) => Color = (award: ThumbnailAward): Color => {
    switch (award) {
      case ThumbnailAward.Gold:
        return Color.Gold;
      case ThumbnailAward.Silver:
        return Color.Silver;
      case ThumbnailAward.Bronze:
        return Color.Bronze;
      case ThumbnailAward.Completed:
        return Color.Green900;
      default: {
        const _: never = award;

        return Color.Green900;
      }
    }
  };

  const isThumbnailAward: (value: string) => value is ThumbnailAward = (value: string): value is ThumbnailAward =>
    Object.values(ThumbnailAward).some((award: ThumbnailAward): boolean => award === value);

  const handleOnCardClick: (item: ContentsUnion) => void = (item: ContentsUnion): void => {
    if ([ContentTypeEnum.files, ContentTypeEnum.contents].includes(item.type)) {
      const hash: string | undefined = getContentHash(item, language);

      if (hash) {
        redirect({ hash, replace: true });
      }
    } else if ([ContentTypeEnum.lesson, ContentTypeEnum.lessonSlideScroll].includes(item.type)) {
      redirect({ route: RouteNameEnum.KnowledgeBaseLesson, params: { id: item.id } });
    } else if (item.type === ContentTypeEnum.video) {
      redirect({ route: RouteNameEnum.VideoPlayer, params: { id: item.id } });
    }
  };

  const getLessonIcons: (item: ContentsUnion) => CardIconsData[] = (item: ContentsUnion): CardIconsData[] => {
    if (!('statusKey' in item)) {
      return [];
    }

    const awardIcon: CardIconsData[] = item.award
      ? [
          {
            name: IconName.Award,
            color: isThumbnailAward(item.award)
              ? getAwardIconColor(item.award)
              : Color.Green900,
            tooltipText: t('competence.app.hooks.lessonCard.passed'),
          },
        ]
      : [];

    switch (item.statusKey) {
      case LessonStatus.NotStarted:
        return [
          { name: IconName.Circle, color: Color.Brand300, tooltipText: t('competence.app.hooks.lessonCard.notStarted') },
          ...awardIcon,
        ];
      case LessonStatus.InProgress:
        return [
          { name: IconName.Time20s, color: Color.Brand300, tooltipText: t('competence.app.hooks.lessonCard.inProgress') },
          ...awardIcon,
        ];
      case LessonStatus.Finished:
        return [
          { name: IconName.CheckmarkCircle, color: Color.Green900, tooltipText: t('competence.app.hooks.lessonCard.completed') },
          ...awardIcon,
        ];
      case LessonStatus.ValidateTo:
        return [
          { name: IconName.CheckmarkCircle, color: Color.Green900, tooltipText: item.status ?? '' },
          ...awardIcon,
        ];
      case LessonStatus.Failed:
        return [
          { name: IconName.RemoveCircleOutlined, color: Color.Red300, tooltipText: t('competence.app.lessonCard.failed') },
          ...awardIcon,
        ];
      default:
        return [];
    }
  };

  const transformThumbnailIntoCardData: (item: ContentsUnion) => CardGroupCard = (item: ContentsUnion): CardGroupCard => ({
    onCardClick: (): void => handleOnCardClick(item),
    onDownloadAsync: item.type === ContentTypeEnum.files
      ? (): Promise<void> => downloadAll(item.name, item.items)
      : item.type === ContentTypeEnum.file
        ? (): Promise<void> => new Promise((resolve: () => void): Promise<void> => downloadFile(item.id).then(resolve))
        : undefined,
    itemsInListNumber: 'items' in item
      ? item.items.length
      : undefined,
    title: item.name,
    category: item.category,
    tags: [
      ...(item.type !== ContentTypeEnum.contents && item.required ? [t('competence.app.hooks.lessonCard.required')] : []),
      ...((item.type === ContentTypeEnum.lessonSlideScroll || item.type === ContentTypeEnum.lesson)
        && item.cyclical ? [t('competence.app.hooks.lessonCard.cyclical')] : []),
    ],
    icons: getLessonIcons(item),
    image: getCardImage(item),
    progress: ('progress' in item && item.progress !== null)
      ? item.progress * 100
      : undefined,
    onIsLikedChange: withoutFavoriteButton
      ? undefined
      : (): Promise<void> => new Promise(
          (resolve: () => void): Promise<void> => toggleFavouriteAsync({
            type: getFavouriteItemType(item.type),
            id: item.id,
            action: item.favorite ? FavouriteItemActionEnum.Remove : FavouriteItemActionEnum.Add,
          }).then(resolve),
        ),
    initialIsLiked: !!item.favorite,
    shouldOpenInNewTab: true,
    variant: viewMode === KnowledgeBaseViewMode.List ? CardVariant.Horizontal : CardVariant.Vertical,
    description: item.type === ContentTypeEnum.contents
      ? undefined
      : item.description ?? undefined,
  });

  const transformThumbnailIntoTableData: (item: ContentsUnion) => ContentTableItem = (item: ContentsUnion): ContentTableItem => ({
    data: [
      {
        type: 'image',
        value: getCardImage(item),
        itemsInListNumber: 'items' in item
          ? item.items.length
          : undefined,
        name: LessonTableColumnName.Image,
        progress: ('progress' in item && item.progress !== null)
          ? item.progress * 100
          : undefined,
      },
      { type: 'title', value: item.name, contentType: item.category, name: LessonTableColumnName.Title },
      {
        type: 'label',
        value: {
          list: [
            ...(item.type !== ContentTypeEnum.contents && item.required
              ? [t('competence.app.hooks.lessonCard.required')]
              : []
            ),
            ...((item.type === ContentTypeEnum.lessonSlideScroll || item.type === ContentTypeEnum.lesson) && item.cyclical
              ? [t('competence.app.hooks.lessonCard.cyclical')]
              : []
            ),
          ],
          colorsData: {
            textColor: Color.Gray600,
            backgroundColor: Color.White,
            borderColor: Color.Gray300,
          },
        },
        name: LessonTableColumnName.Type,
      },
      { icons: getLessonIcons(item), type: 'icons', name: LessonTableColumnName.Status },
      {
        type: 'action',
        value: [
          ...item.type !== ContentTypeEnum.contents && item.description ? [{
            actionType: 'displayDescription' as const,
            description: item.description,
          }] : [],
          {
            actionType: 'toggleFavourite',
            onIsLikedChange: (): Promise<void> => new Promise(
              (resolve: () => void): Promise<void> => toggleFavouriteAsync({
                type: getFavouriteItemType(item.type),
                id: item.id,
                action: item.favorite ? FavouriteItemActionEnum.Remove : FavouriteItemActionEnum.Add,
              }).then(resolve),
            ),
          },
          {
            actionType: 'download',
            onDownloadAsync: item.type === ContentTypeEnum.files
              ? (): Promise<void> => downloadAll(item.name, item.items)
              : item.type === ContentTypeEnum.file
                ? (): Promise<void> => new Promise((resolve: () => void): Promise<void> => downloadFile(item.id).then(resolve))
                : undefined,
            linkData: item.type === ContentTypeEnum.file && item.url
              ? { externalUrl: item.url, fileName: item.name }
              : undefined,
          },
        ],
        name: LessonTableColumnName.Actions,
      },
    ],
    onCardClick: (): void => handleOnCardClick(item),
    initialIsLiked: !!item.favorite,
    type: 'lessonItem',
  });

  return {
    getCardImage,
    handleOnCardClick,
    itemToShowOnSidePanel,
    transformThumbnailIntoCardData,
    transformThumbnailIntoTableData,
    setItemToShowOnSidePanel,
  };
};
